import { useMemo, useRef } from 'react';
import { Chart, setHighcharts, type ChartOptions } from '@highcharts/react';
import Highcharts from 'highcharts/es-modules/masters/highcharts.src.js';
import 'highcharts/es-modules/masters/highcharts-more.src.js';
import 'highcharts/es-modules/masters/modules/accessibility.src.js';
import 'highcharts/es-modules/masters/modules/exporting.src.js';
import 'highcharts/es-modules/masters/modules/export-data.src.js';
import 'highcharts/es-modules/masters/modules/offline-exporting.src.js';
import 'highcharts/es-modules/masters/modules/funnel.src.js';
import 'highcharts/es-modules/masters/modules/pareto.src.js';
import 'highcharts/es-modules/masters/modules/treemap.src.js';
import 'highcharts/es-modules/masters/modules/sankey.src.js';
import 'highcharts/es-modules/masters/modules/xrange.src.js';
import 'highcharts/es-modules/masters/modules/heatmap.src.js';
import 'highcharts/es-modules/masters/modules/pattern-fill.src.js';
import type { ChartThemePayload, ColorVisionMode, ThemeAppearance, VisualPoint, VisualSpec } from '@lognai/plugin-analytics';
import { resolveCategoryColor, categoryColorIndex, resolveThemeLogo } from '@lognai/plugin-analytics/theme-runtime';
import { useTheme } from '@lognai/synapse-design-system-v2';
import { compileChartTheme, DEFAULT_UI_CHART_THEME, themedGradient, themedPointColor, simulateThemeHex } from './chart-theme';

import './theme-fonts.css';
import { MetricVisual } from './MetricVisual';
import { formatChartCategory as formatCategory, formatChartAxisValue, isMonthCategory } from './chart-axis-format';

setHighcharts(Highcharts);

const escapeHtml = (value: string): string =>
  value.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!
  );

const formatValue = (value: number, unit: string, currency = 'USD'): string => {
  if (unit === 'currency') return new Intl.NumberFormat('en-US', { style: 'currency', currency, notation: 'compact' }).format(value);
  if (unit === 'percent') return `${(value * 100).toFixed(1)}%`;
  if (unit === 'days') return `${value.toFixed(1)} days`;
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
};

export interface DrillSelection {
  filter?: import('@lognai/plugin-analytics').SemanticFilter;
  member: string;
  point: VisualPoint;
}

interface Props {
  visual: VisualSpec;
  onSelect?: (selection: DrillSelection) => void;
  height?: number;
  compact?: boolean;
  theme?: ChartThemePayload;
  appearance?: ThemeAppearance;
  colorVisionMode?: ColorVisionMode;
  presentation?: boolean;
  hideExportMenu?: boolean;
  fitHeight?: boolean;
  staticRender?: boolean;
}

export function HighchartsRenderer({ visual, onSelect, height = 360, compact = false, theme = DEFAULT_UI_CHART_THEME, appearance, colorVisionMode = 'normal', presentation = false, hideExportMenu = false, fitHeight = false, staticRender = false }: Props) {
  const { theme: appTheme } = useTheme();
  const resolvedAppearance = appearance ?? appTheme;
  const onSelectRef = useRef(onSelect);
  const categoryMemberRef = useRef(visual.categoryMember);
  onSelectRef.current = visual.categoryMember?.startsWith('comparison.') ? undefined : onSelect;
  categoryMemberRef.current = visual.categoryMember;

  const options = useMemo<ChartOptions>(() => {
    const logo = resolveThemeLogo(theme, resolvedAppearance);
    const reducedMotion = staticRender || typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const compiled = compileChartTheme(theme, resolvedAppearance, visual.template, reducedMotion, colorVisionMode);
    const tokens = compiled.tokens;
    const COLORS = compiled.colors;
    const patternMode = compiled.patternMode === 'auto' && colorVisionMode !== 'normal' ? 'always' : compiled.patternMode;
    const gradient = (from: string) => themedGradient(Highcharts, from, tokens.surface.plotBackground, tokens.series.gradientDirection, tokens.series.fillStyle);
    const seriesColor = (name: string) => {
      const role = name.toLowerCase().replace(/[_-]/g, ' ');
      const comparison = /^(current|current period)$/.test(role) ? tokens.palette.comparison.current : /^(previous|previous period|prior period)$/.test(role) ? tokens.palette.comparison.previous : role === 'target' ? tokens.palette.comparison.target : resolveCategoryColor(theme, resolvedAppearance, '__series__', name);
      return /^(current|current period|previous|previous period|prior period|target)$/.test(role) ? comparison : simulateThemeHex(comparison, colorVisionMode);
    };
    const primary = visual.series[0];
    const categories = primary?.points.map((point) => formatCategory(point.label)) ?? [];
    const plottedValues = visual.series.flatMap((series) => series.points.map((item) => item.value));
    const axisMaximum = Math.max(0, ...plottedValues.filter(Number.isFinite).map(Math.abs));
    const millionCurrencyAxis = primary?.unit === 'currency' && axisMaximum >= 1_000_000;
    const allValuesZero = plottedValues.length > 0 && plottedValues.every((value) => value === 0);
    const selectable = (item: VisualPoint, index: number) => ({
      className: `analytics-chart-point-${index}`,
      custom: { source: item },
      events: {
        click() {
          const member = categoryMemberRef.current;
          if (member) onSelectRef.current?.({ member, point: item });
        },
      },
    });
    const point = (item: VisualPoint, index: number): Highcharts.PointOptionsObject => ({
      name: formatCategory(item.label),
      y: item.value,
      color: themedPointColor(visual.display?.colorByCategory === false ? seriesColor(visual.series[0]?.name ?? '') : visual.categoryMember ? simulateThemeHex(resolveCategoryColor(theme, resolvedAppearance, visual.categoryMember, item.key || item.label), colorVisionMode) : COLORS[index % COLORS.length]!, visual.categoryMember ? categoryColorIndex(visual.categoryMember, item.key || item.label, COLORS.length) : index, patternMode),
      ...selectable(item, index),
    });
    const base: ChartOptions = {
      chart: {
        backgroundColor: tokens.surface.chartBackground,
        plotBackgroundColor: tokens.surface.plotBackground,
        height,
        spacing: compact ? [5, 4, 3, 4] : [tokens.series.spacing + 10, tokens.series.spacing + 8, tokens.series.spacing + 2, tokens.series.spacing],
        animation: compiled.animation,
        style: { fontFamily: compiled.fontFamily },
      },
      title: { text: undefined },
      credits: { enabled: false },
      lang: { accessibility: { chartContainerLabel: '{title}. Interactive chart.' } },
      colors: COLORS,
      legend: {
        ...compiled.legend,
        enabled: !compact && compiled.legend.enabled,
        itemStyle: { color: tokens.surface.text, fontWeight: String(tokens.typography.labelWeight), fontSize: compact && !presentation ? '10px' : compiled.fontSizes.legend },
        itemDistance: compact ? 8 : 20,
        symbolWidth: compact ? 10 : 16,
      },
      accessibility: { enabled: true, description: visual.title },
      exporting: {
        enabled: !compact && !hideExportMenu,
        fallbackToExportServer: false,
        chartOptions: {
          title: { text: escapeHtml(visual.title), style: { color: tokens.surface.text, fontFamily: compiled.fontFamily } },
          subtitle: { text: theme.brand?.name ? escapeHtml(theme.brand.name) : undefined, style: { color: tokens.surface.mutedText } },
          caption: { text: theme.presentation?.footerText ? escapeHtml(theme.presentation.footerText) : undefined, style: { color: tokens.surface.mutedText } },
          chart: {
            spacingTop: logo ? 60 : 20,
            events: logo ? { load() { this.renderer.image(logo, Math.max(10, this.chartWidth - 140), 8, 120, 44).add(); } } : undefined,
          },
        },
        buttons: {
          contextButton: {
            menuItems: [
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
            ],
            symbolStroke: tokens.surface.mutedText,
            symbolStrokeWidth: 1.5,
            theme: { fill: 'transparent', stroke: 'transparent' },
          },
        },
      },
      tooltip: {
        shared: visual.template === 'area' || visual.template === 'stacked_area' || visual.template === 'stacked_column',
        useHTML: true,
        borderWidth: 0,
        shadow: tokens.components.tooltipStyle === 'elevated',
        backgroundColor: tokens.surface.tooltipBackground,
        style: { color: tokens.surface.tooltipText },
        formatter() {
          const points = this.points ?? [this];
          const body = points.map((entry) => {
            const series = visual.series.find((candidate) => candidate.name === entry.series.name) ?? primary;
            const value = visual.template === 'diverging_bar' ? Math.abs(entry.y ?? 0) : (entry.y ?? 0);
            return `<div style="display:flex;justify-content:space-between;gap:24px"><span>${escapeHtml(entry.series.name)}</span><b>${formatValue(value, series?.unit ?? 'number', series?.currency)}</b></div>`;
          }).join('');
          return `<div style="min-width:180px;color:${tokens.surface.tooltipText}"><b>${escapeHtml(formatCategory(String(this.key ?? '')))}</b>${body}</div>`;
        },
      },
      xAxis: { categories, lineColor: tokens.surface.axis, tickColor: tokens.surface.axis, labels: { style: { color: tokens.surface.mutedText, fontSize: compact && !presentation ? '10px' : compiled.fontSizes.axis, textOverflow: 'ellipsis' } } },
      yAxis: { min: 0, max: allValuesZero ? 1 : undefined, startOnTick: false, endOnTick: false, title: { text: !compact && millionCurrencyAxis ? `${primary.currency ?? 'USD'} (millions)` : undefined }, gridLineColor: tokens.surface.grid, gridLineWidth: compiled.gridLineWidth, labels: { formatter() { return formatChartAxisValue(Number(this.value), primary?.unit ?? 'number', axisMaximum); }, style: { color: tokens.surface.mutedText, fontSize: compact && !presentation ? '10px' : compiled.fontSizes.axis, textOverflow: 'ellipsis' } } },
      plotOptions: {
        series: {
          animation: compiled.animation,
          cursor: onSelect ? 'pointer' : 'default',
          borderWidth: tokens.series.borderWidth,
          opacity: tokens.series.opacity,
          dataLabels: {
            enabled: compiled.dataLabelsEnabled,
            formatter() {
              const series = visual.series.find((candidate) => candidate.name === this.series.name) ?? primary;
              return formatValue(this.y ?? 0, series?.unit ?? 'number', series?.currency);
            },
            style: { color: tokens.surface.text, fontSize: compiled.fontSizes.data, fontWeight: String(tokens.typography.labelWeight), textOutline: 'none' },
          },
          states: { hover: { brightness: tokens.interaction.hoverBrightness }, inactive: { opacity: tokens.interaction.inactiveOpacity } },
        },
        bar: { borderRadius: tokens.series.borderRadius },
        column: { borderRadius: tokens.series.borderRadius },
      },
    };

    if (!primary) return base;
    if (visual.template === 'area') {
      base.series = visual.series.map((series) => ({
        type: 'areaspline',
        name: series.name,
        data: series.points.map((item, pointIndex) => { const data = point(item, pointIndex); return ['area', 'stacked_area', 'stacked_column', 'diverging_bar'].includes(visual.template) || (visual.template === 'horizontal_bar' && visual.series.length > 1) ? { ...data, color: undefined } : data; }),
        color: seriesColor(series.name),
        fillColor: gradient(seriesColor(series.name)),
        lineWidth: tokens.series.lineWidth,
        marker: { enabled: tokens.series.marker !== 'none', symbol: tokens.series.marker === 'none' ? 'circle' : tokens.series.marker, states: { hover: { enabled: true, radius: 5 } } },
      }));
    } else if (visual.template === 'stacked_area') {
      base.plotOptions = { ...base.plotOptions, area: { stacking: 'normal', marker: { enabled: false } } };
      base.series = visual.series.map((series) => ({
        type: 'area',
        name: series.name,
        data: series.points.map((item, pointIndex) => { const data = point(item, pointIndex); return ['area', 'stacked_area', 'stacked_column', 'diverging_bar'].includes(visual.template) || (visual.template === 'horizontal_bar' && visual.series.length > 1) ? { ...data, color: undefined } : data; }),
        color: seriesColor(series.name),
        fillColor: gradient(seriesColor(series.name)),
        lineWidth: tokens.series.lineWidth,
      }));
    } else if (visual.template === 'horizontal_bar') {
      base.chart = { ...base.chart, type: 'bar', height };
      base.series = visual.series.map((series) => ({
        type: 'bar', name: series.name, data: series.points.map((item, pointIndex) => { const data = point(item, pointIndex); return ['area', 'stacked_area', 'stacked_column', 'diverging_bar'].includes(visual.template) || (visual.template === 'horizontal_bar' && visual.series.length > 1) ? { ...data, color: undefined } : data; }), color: gradient(seriesColor(series.name)),
      }));
    } else if (visual.template === 'diverging_bar') {
      base.chart = { ...base.chart, type: 'bar', height: compact ? height : Math.max(360, categories.length * 42) };
      base.yAxis = {
        title: { text: undefined },
        gridLineColor: tokens.surface.grid,
        gridLineWidth: compiled.gridLineWidth,
        labels: { formatter() { return new Intl.NumberFormat('en-US', { notation: 'compact' }).format(Math.abs(Number(this.value))); } },
        plotLines: [{ value: 0, color: tokens.surface.axis, width: 1, zIndex: 4 }],
      };
      base.plotOptions = { ...base.plotOptions, bar: { grouping: false, pointPadding: 0.13 } };
      base.series = visual.series.map((series, index) => ({
        type: 'bar',
        name: series.name,
        data: series.points.map((item, pointIndex) => ({
          ...point(item, pointIndex),
          y: index === 0 ? -item.value : item.value,
        })),
        color: index === 0 ? tokens.palette.diverging.negative : tokens.palette.diverging.positive,
      }));
    } else if (visual.template === 'stacked_column') {
      base.plotOptions = { ...base.plotOptions, column: { stacking: visual.display?.groupedColumns ? undefined : 'normal' } };
      base.series = visual.series.map((series) => ({
        type: 'column', name: series.name, data: series.points.map((item, pointIndex) => { const data = point(item, pointIndex); return ['area', 'stacked_area', 'stacked_column', 'diverging_bar'].includes(visual.template) || (visual.template === 'horizontal_bar' && visual.series.length > 1) ? { ...data, color: undefined } : data; }), color: gradient(seriesColor(series.name)),
      }));
    } else if (visual.template === 'donut') {
      base.series = [{
        type: 'pie', name: primary.name, innerSize: '58%', data: primary.points.map(point),
        dataLabels: compact
          ? { enabled: true, format: '<b>{point.percentage:.0f}%</b>', distance: -22, style: { color: tokens.surface.tooltipText, fontSize: presentation ? compiled.fontSizes.data : '9px', textOutline: 'none' } }
          : { enabled: tokens.components.labelDensity !== 'minimal', format: '{point.name}<br><b>{point.percentage:.0f}%</b>', distance: 14, style: { color: tokens.surface.text, textOutline: 'none' } },
      }];
    } else if (visual.template === 'treemap') {
      const treemapPoints = primary.points.filter((item) => Number.isFinite(item.value) && item.value > 0);
      base.chart = { ...base.chart, type: 'treemap', height, spacing: compact ? [3, 3, 3, 3] : [8, 8, 8, 8] };
      base.xAxis = { visible: false };
      base.yAxis = { visible: false };
      base.legend = { enabled: false };
      base.tooltip = {
        useHTML: true,
        borderWidth: 0,
        formatter() {
          return `<div style="min-width:150px"><b>${escapeHtml(String(this.name ?? ''))}</b><br>${formatValue(Number(this.value ?? 0), primary.unit, primary.currency)}</div>`;
        },
      };
      base.series = [{
        type: 'treemap',
        name: primary.name,
        layoutAlgorithm: 'squarified',
        alternateStartingDirection: true,
        colorByPoint: true,
        data: treemapPoints.map((item, index) => ({
          name: formatCategory(item.label),
          value: item.value,
          color: themedPointColor(visual.display?.colorByCategory === false ? seriesColor(visual.series[0]?.name ?? '') : visual.categoryMember ? simulateThemeHex(resolveCategoryColor(theme, resolvedAppearance, visual.categoryMember, item.key || item.label), colorVisionMode) : COLORS[index % COLORS.length]!, visual.categoryMember ? categoryColorIndex(visual.categoryMember, item.key || item.label, COLORS.length) : index, patternMode),
          ...selectable(item, index),
        })),
        dataLabels: {
          enabled: true,
          format: compact ? '<b>{point.name}</b>' : '<b>{point.name}</b><br>{point.value:,.0f}',
          crop: true,
          overflow: 'justify',
          padding: compact ? 2 : 5,
          style: { color: tokens.surface.tooltipText, textOutline: 'none', fontWeight: String(tokens.typography.labelWeight), ...(compact ? { fontSize: presentation ? compiled.fontSizes.data : '9px' } : {}) },
        },
      }];
    } else if (visual.template === 'sankey') {
      base.chart = { ...base.chart, height: compact ? height : 390 };
      base.series = [{
        type: 'sankey',
        name: primary.name,
        keys: ['from', 'to', 'weight'],
        data: primary.points.map((item, index) => ({
          from: item.source ?? 'Unknown status',
          to: item.target ?? 'Unknown blocker',
          weight: item.value,
          ...selectable(item, index),
        })),
        nodes: [...new Set(primary.points.flatMap((item) => [item.source ?? 'Unknown status', item.target ?? 'Unknown blocker']))].map((id) => ({ id, color: simulateThemeHex(resolveCategoryColor(theme, resolvedAppearance, visual.categoryMember ?? '__category__', id), colorVisionMode) })),
        nodeWidth: 18,
        nodePadding: 22,
        dataLabels: { enabled: tokens.components.labelDensity !== 'minimal', style: { color: tokens.surface.text, textOutline: 'none', fontWeight: String(tokens.typography.labelWeight), ...(compact ? { fontSize: presentation ? compiled.fontSizes.data : '9px' } : {}) } },
      }];
    } else if (visual.template === 'funnel') {
      base.series = [{
        type: 'funnel', name: primary.name, data: primary.points.map(point), neckWidth: '32%', neckHeight: '22%', width: '78%',
        dataLabels: compact
          ? { enabled: true, softConnector: false, distance: 3, format: '{point.name}: {point.y:,.0f}', style: { fontSize: presentation ? compiled.fontSizes.data : '9px', textOutline: 'none' } }
          : { enabled: true, softConnector: true, format: '<b>{point.name}</b>: {point.y:,.0f}' },
      }];
    } else if (visual.template === 'pareto') {
      base.chart = { ...base.chart, height: compact ? height : Math.max(360, categories.length * 34) };
      base.yAxis = [
        { title: { text: primary.name }, gridLineColor: tokens.surface.grid, gridLineWidth: compiled.gridLineWidth, labels: { style: { color: tokens.surface.mutedText } } },
        { title: { text: 'Cumulative share' }, max: 100, min: 0, opposite: true, labels: { format: '{value}%' } },
      ];
      base.series = [
        { type: 'column', id: 'primary', name: primary.name, data: primary.points.map(point), color: gradient(COLORS[0]!) },
        { type: 'pareto', name: 'Cumulative share', baseSeries: 'primary', yAxis: 1, color: tokens.palette.comparison.target, zIndex: 3 },
      ];
    } else if (visual.template === 'bubble') {
      const allXZero = primary.points.every((item) => (item.x ?? 0) === 0);
      const allYZero = primary.points.every((item) => (item.y ?? 0) === 0);
      base.xAxis = { min: 0, max: allXZero ? 1 : undefined, startOnTick: false, endOnTick: false, title: { text: visual.series[0]?.name }, gridLineColor: tokens.surface.grid, gridLineWidth: compiled.gridLineWidth, labels: { style: { color: tokens.surface.mutedText } } };
      base.yAxis = { min: 0, max: allYZero ? 1 : undefined, startOnTick: false, endOnTick: false, title: { text: visual.series[1]?.name }, gridLineColor: tokens.surface.grid, gridLineWidth: compiled.gridLineWidth, labels: { style: { color: tokens.surface.mutedText } } };
      base.series = [{
        type: 'bubble',
        name: 'Supplier risk',
        minSize: 12,
        maxSize: 64,
        data: primary.points.map((item, index) => ({
          x: item.x, y: item.y, z: item.z, name: formatCategory(item.label), color: Highcharts.color(simulateThemeHex(resolveCategoryColor(theme, resolvedAppearance, visual.categoryMember ?? '__category__', item.key || item.label), colorVisionMode)).setOpacity(tokens.series.opacity).get(),
          ...selectable(item, index),
        })),
        dataLabels: { enabled: true, format: '{point.name}', allowOverlap: false },
      }];
    } else if (visual.template === 'gantt') {
      const ganttPoints = primary.points.filter((item) => item.x != null && item.x2 != null);
      base.chart = { ...base.chart, type: 'xrange', height: compact ? height : Math.max(360, ganttPoints.length * 34) };
      base.xAxis = { type: 'datetime', title: { text: undefined }, gridLineColor: tokens.surface.grid, gridLineWidth: compiled.gridLineWidth, labels: { style: { color: tokens.surface.mutedText } } };
      base.yAxis = {
        categories: ganttPoints.map((item) => formatCategory(item.label)),
        title: { text: undefined },
        reversed: true,
      };
      base.tooltip = {
        useHTML: true,
        formatter() {
          const start = Highcharts.dateFormat('%b %e, %Y', Number(this.x));
          const end = Highcharts.dateFormat('%b %e, %Y', Number((this as Highcharts.Point & { x2?: number }).x2));
          return `<b>${escapeHtml(String(this.name ?? ''))}</b><br>${start} → ${end}`;
        },
      };
      base.series = [{
        type: 'xrange',
        name: 'Arrival window',
        pointWidth: 14,
        borderRadius: 5,
        data: ganttPoints.map((item, index) => ({
          x: item.x!,
          x2: item.x2!,
          y: index,
          name: formatCategory(item.label),
          color: themedPointColor(visual.display?.colorByCategory === false ? seriesColor(visual.series[0]?.name ?? '') : visual.categoryMember ? simulateThemeHex(resolveCategoryColor(theme, resolvedAppearance, visual.categoryMember, item.key || item.label), colorVisionMode) : COLORS[index % COLORS.length]!, visual.categoryMember ? categoryColorIndex(visual.categoryMember, item.key || item.label, COLORS.length) : index, patternMode),
          ...selectable(item, index),
        })),
      }];
    } else if (visual.template === 'heatmap') {
      const ageOrder = ['0-7 days', '8-30 days', '31-60 days', '61-90 days', '91+ days', 'Unknown'];
      const priorityOrder = ['urgent', 'high', 'normal', 'low', 'Unknown'];
      const xValues = [...new Set(primary.points.map((item) => item.xLabel ?? 'Unknown'))];
      const yValues = [...new Set(primary.points.map((item) => item.yLabel ?? 'Unknown'))];
      const xCategories = [...ageOrder.filter((item) => xValues.includes(item)), ...xValues.filter((item) => !ageOrder.includes(item))];
      const monthAxis = xValues.length > 0 && xValues.every(isMonthCategory);
      if (monthAxis) xCategories.sort();
      const yCategories = [...priorityOrder.filter((item) => yValues.includes(item)), ...yValues.filter((item) => !priorityOrder.includes(item))];
      base.chart = { ...base.chart, type: 'heatmap', height: compact ? height : Math.max(340, yCategories.length * 64) };
      base.xAxis = { categories: xCategories.map(formatCategory), title: { text: compact ? undefined : monthAxis ? 'Month' : xValues.some(value => ageOrder.includes(value) && value !== 'Unknown') ? 'Order age' : undefined }, lineColor: tokens.surface.axis, labels: { step: monthAxis && xCategories.length <= 12 ? 1 : undefined, style: { color: tokens.surface.mutedText, fontSize: compact && !presentation ? '9px' : compiled.fontSizes.axis } } };
      base.yAxis = { categories: yCategories.map(formatCategory), title: { text: compact || !yValues.some(value => priorityOrder.includes(value) && value !== 'Unknown') ? undefined : 'Priority' }, reversed: true, labels: { style: { color: tokens.surface.mutedText, fontSize: compact && !presentation ? '9px' : compiled.fontSizes.axis } } };
      base.colorAxis = { min: 0, minColor: tokens.palette.sequential.low, maxColor: tokens.palette.sequential.high };
      base.series = [{
        type: 'heatmap',
        name: primary.name,
        data: primary.points.map((item, index) => ({
          x: xCategories.indexOf(item.xLabel ?? 'Unknown'),
          y: yCategories.indexOf(item.yLabel ?? 'Unknown'),
          value: item.value,
          ...selectable(item, index),
        })),
        dataLabels: { enabled: true, format: '{point.value:,.0f}', style: { textOutline: 'none', fontWeight: '600' } },
      }];
    } else if (visual.template === 'calendar_heatmap') {
      const dated = primary.points
        .filter((item) => item.x != null)
        .sort((a, b) => (a.x ?? 0) - (b.x ?? 0));
      const first = dated[0]?.x ?? Date.now();
      const firstDate = new Date(first);
      const mondayOffset = (firstDate.getUTCDay() + 6) % 7;
      const calendarStart = Date.UTC(firstDate.getUTCFullYear(), firstDate.getUTCMonth(), firstDate.getUTCDate() - mondayOffset);
      const weekIndex = (timestamp: number) => Math.floor((timestamp - calendarStart) / 604_800_000);
      const weekCount = Math.max(1, ...dated.map((item) => weekIndex(item.x!))) + 1;
      const weekLabels = Array.from({ length: weekCount }, (_, index) => Highcharts.dateFormat('%b %e', calendarStart + index * 604_800_000));
      base.chart = { ...base.chart, type: 'heatmap', height: compact ? height : Math.max(360, weekCount * 34 + 90) };
      base.xAxis = { categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], opposite: true, lineWidth: 0 };
      base.yAxis = { categories: weekLabels, title: { text: undefined }, reversed: true, gridLineWidth: 0 };
      base.colorAxis = { min: 0, minColor: tokens.palette.sequential.low, maxColor: tokens.palette.sequential.high };
      base.tooltip = {
        useHTML: true,
        formatter() {
          const source = (this.options.custom as { source?: VisualPoint } | undefined)?.source;
          const date = source?.x != null ? Highcharts.dateFormat('%a, %b %e, %Y', source.x) : '';
          return `<b>${date}</b><br>${formatValue(source?.value ?? 0, primary.unit, primary.currency)} ${escapeHtml(primary.name)}`;
        },
      };
      base.series = [{
        type: 'heatmap',
        name: primary.name,
        borderWidth: 3,
        borderColor: tokens.surface.chartBackground,
        data: dated.map((item, index) => {
          const date = new Date(item.x!);
          return {
            x: (date.getUTCDay() + 6) % 7,
            y: weekIndex(item.x!),
            value: item.value,
            ...selectable(item, index),
          };
        }),
        dataLabels: { enabled: false },
      }];
    }
    if (fitHeight) base.chart = { ...base.chart, height };
    return base;
  }, [colorVisionMode, compact, presentation, hideExportMenu, fitHeight, staticRender, height, onSelect, resolvedAppearance, theme, visual]);

  if (['metric_panel', 'percentage_bar', 'share_bar', 'bullet', 'table'].includes(visual.template)) return <MetricVisual visual={visual} theme={theme} appearance={resolvedAppearance} height={height} onSelect={onSelectRef.current} />;

  return (
    <div>
      <Chart
        key={visual.categoryMember ?? visual.title}
        options={options}
        containerProps={{
          className: 'w-full',
          style: { minHeight: height },
          ...({ 'data-category-member': visual.categoryMember } as Record<string, string | undefined>),
        }}
      />
      {visual.display?.note && <p className="px-3 pb-2 text-xs leading-5 text-muted-foreground">{visual.display.note}</p>}
    </div>
  );
}
